// HomeFeed.tsx
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { type VirtualItem, useWindowVirtualizer } from '@tanstack/react-virtual'
import { useQueryClient } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'
import styled from 'styled-components'

import ScrollToTopFab from 'components/ScrollToTop'
import CaughtUpMessage from 'components/UI/CaughtUpMessage/CaughtUpMessage'
import { EmptyFeedPost } from 'components-ref/molecules'
import { PostViewWrapper } from 'features/shared/components/PostViewWrapper/PostViewWrapper'
import CustomPullToRefresh from 'helpers/customPullToRefreshHome'
import { getFirstPagePostsFromInfiniteData } from 'helpers/feed/getFirstPagePostsHome'
import { useFilterQuery, useHomePostsFeed } from 'helpers/hooks'
import { useImpressionsTracker } from 'helpers/hooks/feed/useImpressionsTracker'
import { prefetchUpcomingPosts } from 'helpers/prefetchPosts'
import { useUserProfile } from 'store'
import { useFollowingFeedCacheStore } from 'store/followingFeedCacheStore'
import type { IPost } from 'types/interfaces/ITypes'

import { HomeFeedSkeleton } from './HomeFeed.skeleton'

type HomeFeedProps = {
  scrollContainerRef: React.RefObject<HTMLDivElement>
  listId: string
  onInitialLoadSettled?: () => void
}

const ESTIMATED_ROW_PX = 302
const OVERSCAN = 3
const SKELETON_DELAY_MS = 180

// prefetch tuning (ONE AHEAD)
const PREFETCH_AHEAD = 1
const PREFETCH_THROTTLE_MS = 120

type RowProps = {
  post: IPost
  index: number
  dataQuery: readonly any[]
  userId?: string
  addDebounceImpression: (postId: string, isOther?: boolean) => void
  userData: any
}

function dedupePosts<T extends { id: any }>(posts: T[]): T[] {
  const seen: Record<string, 1> = {}
  const out: T[] = []
  for (let i = 0; i < posts.length; i++) {
    const p = posts[i]
    const id = (p as any)?.id
    if (id == null) continue
    const key = String(id)
    if (seen[key]) continue
    seen[key] = 1
    out.push(p)
  }
  return out
}

function mergePreferNext<T extends { id: any }>(prev: T[], next: T[]): T[] {
  const map = new Map<string, T>()

  // start with prev
  for (let i = 0; i < prev.length; i++) {
    const p = prev[i]
    const id = (p as any)?.id
    if (id == null) continue
    map.set(String(id), p)
  }

  // override with next
  for (let i = 0; i < next.length; i++) {
    const p = next[i]
    const id = (p as any)?.id
    if (id == null) continue
    map.set(String(id), p)
  }

  // preserve order: prev order, but swapped to next object when present; then append new next
  const out: T[] = []
  const seen = new Set<string>()

  for (let i = 0; i < prev.length; i++) {
    const id = (prev[i] as any)?.id
    if (id == null) continue
    const key = String(id)
    if (seen.has(key)) continue
    seen.add(key)
    const resolved = map.get(key)
    if (resolved) out.push(resolved)
  }

  for (let i = 0; i < next.length; i++) {
    const id = (next[i] as any)?.id
    if (id == null) continue
    const key = String(id)
    if (seen.has(key)) continue
    seen.add(key)
    const resolved = map.get(key)
    if (resolved) out.push(resolved)
  }

  return out
}

const FeedRow = memo(
  function FeedRow({ post, index, dataQuery, addDebounceImpression, userData, userId }: RowProps) {
    
    return (
      <PostViewWrapper
        post={post}
        index={index}
        addDebounceImpression={addDebounceImpression}
        userData={userData}
        userId={userId}
        dataQuery={dataQuery}
      />
    )
  },
  (prev, next) =>
    prev.post === next.post &&
    prev.index === next.index &&
    prev.userId === next.userId &&
    prev.userData === next.userData &&
    prev.addDebounceImpression === next.addDebounceImpression &&
    prev.dataQuery === next.dataQuery
)
FeedRow.displayName = 'FeedRow'

export const HomeFeed = ({ scrollContainerRef, listId,
  onInitialLoadSettled }: HomeFeedProps) => {
  const userData = useUserProfile()
  const userId = userData?.id
  const { addDebounceImpression } = useImpressionsTracker()

  const filter = useFilterQuery()
  const type = filter.get('type') ?? 'all'
  const effectiveListId = listId ?? 'all'

  const hardRefreshRef = useRef(false)
  const queryClient = useQueryClient()
  const cacheKey = useMemo(() => `${effectiveListId}:${type}:full`, [effectiveListId, type])
  const getPageScrollElement = useCallback(() => {
    return (document.scrollingElement as HTMLElement) || document.documentElement
  }, [])
  const appendingRef = useRef(false)
  
  const getFeed = useFollowingFeedCacheStore((s) => s.getFeed)
  const setFeed = useFollowingFeedCacheStore((s) => s.setFeed)

  const [feedState, setFeedState] = useState<{ key: string; posts: IPost[] }>(() => {
    const cached = (getFeed(cacheKey) ?? []) as IPost[]
    return { key: cacheKey, posts: dedupePosts(cached) }
  })
  const [resolvedKey, setResolvedKey] = useState('')

  const hasHydrated = useFollowingFeedCacheStore((s) => s.hasHydrated)
  const getScrollPos = useFollowingFeedCacheStore((s) => s.getScrollPos)
  const setScrollPos = useFollowingFeedCacheStore((s) => s.setScrollPos)
  const didRestoreScrollRef = useRef(false)
  const ignoreChromeScrollUntilRef = useRef(0)

  const stateMatchesKey = feedState.key === cacheKey
  const feedPosts = stateMatchesKey ? feedState.posts : []
  const dataQuery = useMemo<any[]>(
    () => ['getHomeFeed', effectiveListId, type],
    [effectiveListId, type]
  )

const {
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  isSuccess,
  isError
} = useHomePostsFeed({
  list: effectiveListId,
  type,
  per_page: 15,
  enabled: true
})

useEffect(() => {
  if (isSuccess || isError) {
    onInitialLoadSettled?.()
  }
}, [isSuccess, isError, onInitialLoadSettled])

  const incomingPosts = useMemo<IPost[]>(() => {
    const d: any = data as any
    if (!d) return []

    if (Array.isArray(d.posts)) return d.posts as IPost[]

    const pages = d.pages
    if (!Array.isArray(pages)) return []

    const out: IPost[] = []
    for (let i = 0; i < pages.length; i++) {
      const p = pages[i]

      const a = p?.page?.data?.data
      if (Array.isArray(a)) {
        out.push(...(a as IPost[]))
        continue
      }

      const b = p?.posts
      if (Array.isArray(b)) {
        out.push(...(b as IPost[]))
        continue
      }

      const c = p?.data
      if (Array.isArray(c)) out.push(...(c as IPost[]))
    }

    return out
  }, [data])

  // ---- resolution gate (anti-flicker)
  const hasSomethingToShow = feedPosts.length > 0

  // hydrate from cache on key change
  useLayoutEffect(() => {
    const cached = (getFeed(cacheKey) ?? []) as IPost[]
    setFeedState({ key: cacheKey, posts: dedupePosts(cached) })
  }, [cacheKey, getFeed])


  // real stale-result protection
  const requestVersionRef = useRef(0)
  useEffect(() => {
    requestVersionRef.current += 1
  }, [cacheKey])

  const fetchNextPageSafe = useCallback(async () => {
    appendingRef.current = true
    return await fetchNextPage()
  }, [fetchNextPage])

useEffect(() => {
  setResolvedKey('')
}, [cacheKey])

useEffect(() => {
  if (isLoading) return

  const myVersion = requestVersionRef.current
  const keyAtSchedule = cacheKey

  queueMicrotask(() => {
    if (requestVersionRef.current !== myVersion) return
    if (keyAtSchedule !== cacheKey) return

    setResolvedKey(cacheKey)

    const next = dedupePosts(incomingPosts)

    let finalPosts: IPost[] = next

    setFeedState((prev) => {
      if (prev.key !== cacheKey) {
        finalPosts = next
        return { key: cacheKey, posts: next }
      }

      if (hardRefreshRef.current) {
        hardRefreshRef.current = false
        finalPosts = next
        return { key: cacheKey, posts: next }
      }

      if (appendingRef.current) {
        appendingRef.current = false

        if (next.length > 0) {
          finalPosts = mergePreferNext(prev.posts, next)
          return { key: cacheKey, posts: finalPosts }
        }

        finalPosts = prev.posts
        return prev
      }

      const sameIds =
        prev.posts.length === next.length &&
        prev.posts.every((post, index) => post.id === next[index]?.id)

      if (sameIds) {
        const merged = prev.posts.map((prevPost, index) => {
          const nextPost = next[index]
          if (!nextPost) return prevPost

          return { ...prevPost, ...nextPost }
        })

        const changed = merged.some((post, index) => post !== prev.posts[index])

        if (!changed) {
          finalPosts = prev.posts
          return prev
        }

        finalPosts = merged
        return { key: cacheKey, posts: merged }
      }

      finalPosts = next
      return { key: cacheKey, posts: next }
    })

    setFeed(cacheKey, finalPosts)
  })
}, [isLoading, incomingPosts, cacheKey, setFeed])



// HIDE FOOTER/HEADER WHEN SCROLLING DOWN
useEffect(() => {
  let lastTop = window.scrollY
  let ticking = false

  const SHOW_AT_TOP_PX = 20
  const HIDE_AFTER_SCROLL_PX = 80
  const DELTA_PX = 8

  const setChromeHidden = (hidden: boolean) => {
    const isMobile = window.innerWidth <= 768

    document.body.classList.toggle(
      'footer-hidden',
      isMobile && hidden
    )

    document.body.classList.toggle(
      'header-hidden',
      isMobile && hidden
    )
  }
  const onScroll = () => {

      if (Date.now() < ignoreChromeScrollUntilRef.current) {
    setChromeHidden(false)
    lastTop = window.scrollY
    return
  }
    const top = window.scrollY
    const delta = top - lastTop

    if (!ticking) {
      ticking = true

  requestAnimationFrame(() => {
    ticking = false



    if (top <= SHOW_AT_TOP_PX || top < HIDE_AFTER_SCROLL_PX) {
      setChromeHidden(false)
      lastTop = top
      return
    }

    if (Math.abs(delta) < DELTA_PX) {
      lastTop = top
      return
    }

    setChromeHidden(delta > 0)
    lastTop = top
  })
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  setChromeHidden(false)

  return () => {
    window.removeEventListener('scroll', onScroll)
    setChromeHidden(false)
  }
}, [])

  // virtualizer
const rowVirtualizer = useWindowVirtualizer({
  count: feedPosts.length,

estimateSize: (index) => {
  const post = feedPosts[index]
  if (!post) return ESTIMATED_ROW_PX

  const media = [
    ...(post.photos ?? []),
    ...(post.videos ?? []),
    ...(post.photos_preview ?? []),
    ...(post.videos_preview ?? [])
  ]

  const hasPortrait = media.some(
    (item) => item.orientation?.toLowerCase() === 'portrait'
  )

  if (hasPortrait) return 576
  if (media.length) return 295

  return ESTIMATED_ROW_PX
},

  overscan: OVERSCAN,
  getItemKey: (index) => {
    const id = (feedPosts[index] as any)?.id
    return id != null ? String(id) : String(index)
  },
})
useEffect(() => {
  didRestoreScrollRef.current = false
}, [cacheKey])

useLayoutEffect(() => {
  if (!hasHydrated) return
  if (didRestoreScrollRef.current) return
  if (!stateMatchesKey) return
  if (!feedPosts.length) return

  const saved = getScrollPos(cacheKey)

  didRestoreScrollRef.current = true

  if (saved == null || saved <= 0) return
ignoreChromeScrollUntilRef.current = Date.now() + 500
document.body.classList.remove('footer-hidden')
document.body.classList.remove('header-hidden')
  requestAnimationFrame(() => {
    window.scrollTo({
      top: saved,
      left: 0,
      behavior: 'auto',
    })

    requestAnimationFrame(() => {
      window.scrollTo({
        top: saved,
        left: 0,
        behavior: 'auto',
      })
    })
  })
}, [
  hasHydrated,
  cacheKey,
  stateMatchesKey,
  feedPosts.length,
  getScrollPos,
])

useEffect(() => {
  if (!hasHydrated) return
  if (!stateMatchesKey) return
  if (!feedPosts.length) return

  let frame = 0

  const save = () => {
    if (frame) return

    frame = window.requestAnimationFrame(() => {
      frame = 0

      const top =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0

      setScrollPos(cacheKey, top)
    })
  }

  window.addEventListener('scroll', save, { passive: true })

  return () => {
    window.removeEventListener('scroll', save)

    if (frame) {
      window.cancelAnimationFrame(frame)
    }

    const top =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0

    setScrollPos(cacheKey, top)
  }
}, [hasHydrated, cacheKey, stateMatchesKey, feedPosts.length, setScrollPos])


  // measure real row heights (like PostsFeed)
  const measureRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      if (!el) return
      // keep TanStack happy + enables measureElement
      el.setAttribute('data-index', String(index))
      rowVirtualizer.measureElement(el)
    },
    [rowVirtualizer]
  )

  // skeleton anti-flicker
  const [allowSkeleton, setAllowSkeleton] = useState(false)
  useEffect(() => {
    setAllowSkeleton(false)
    const t = window.setTimeout(() => setAllowSkeleton(true), SKELETON_DELAY_MS)
    return () => window.clearTimeout(t)
  }, [cacheKey])

  const isResolvedForThisKey = stateMatchesKey && resolvedKey === cacheKey
  const showEmpty = isResolvedForThisKey && !isLoading && feedPosts.length === 0
  const showSkeleton = allowSkeleton && !isResolvedForThisKey && !hasSomethingToShow


  // PULL TO REFRESH LOGIC

const pullToRefreshHome = async () => {
  hardRefreshRef.current = true

  const q = queryClient.getQueryCache().find(dataQuery as any)

  if (!q) {
    await queryClient.refetchQueries(dataQuery as any, {
      active: true,
      inactive: true,
    })
    return
  }

  const key = q.queryKey
  const old = queryClient.getQueryData<any>(key)

  const firstPagePosts = dedupePosts(getFirstPagePostsFromInfiniteData(old))

  queryClient.setQueryData(key, (prev: any) => {
    if (!prev?.pages?.length) return prev

    return {
      ...prev,
      pages: [prev.pages[0]],
      pageParams: [prev.pageParams?.[0]],
    }
  })

  setFeedState({
    key: cacheKey,
    posts: firstPagePosts,
  })

  setFeed(cacheKey, firstPagePosts)

  const el = getPageScrollElement()
  el.scrollTop = 0
setScrollPos(cacheKey, 0)
  await queryClient.refetchQueries(key, {
    active: true,
    inactive: false,
    refetchPage: (_page: any, index: number) => index === 0,
  } as any)
}

  // fetch next page
  const fetchInFlightRef = useRef(false)
  useEffect(() => {
    if (!isFetchingNextPage) fetchInFlightRef.current = false
  }, [isFetchingNextPage])


  const getPrefetchWindow = useFollowingFeedCacheStore((s) => s.getPrefetchWindow)
  const setPrefetchWindow = useFollowingFeedCacheStore((s) => s.setPrefetchWindow)

const prefetchAbortRef = useRef<AbortController | null>(null)
const prefetchTimerRef = useRef<number | null>(null)

useEffect(() => {
  const tryFetch = () => {
    if (!hasNextPage) return
    if (isFetchingNextPage) return
    if (fetchInFlightRef.current) return
    if (!feedPosts.length) return

    const items = rowVirtualizer.getVirtualItems()
    if (!items.length) return

    const lastVisibleIndex = items[items.length - 1].index

    // within 5 posts of the end
    if (lastVisibleIndex < feedPosts.length - 5) return

    fetchInFlightRef.current = true
    fetchNextPageSafe()
  }

  tryFetch()

  const onScroll = () => tryFetch()

  window.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    window.removeEventListener('scroll', onScroll)
  }
}, [
  feedPosts.length,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPageSafe,
  rowVirtualizer,
])


useEffect(() => {
  if (!feedPosts.length) return

  const schedulePrefetch = () => {
    if (prefetchTimerRef.current != null) return

    prefetchTimerRef.current = window.setTimeout(() => {
      prefetchTimerRef.current = null

      if (!feedPosts.length) return

      const items = rowVirtualizer.getVirtualItems()
      if (!items.length) return

      const top =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0

      const bottom = top + window.innerHeight

      let lastVisibleIndex = items[0].index

      for (let i = 0; i < items.length; i++) {
        const it = items[i]
        const itTop = it.start
        const itBottom = it.start + it.size

        const intersects = itBottom > top && itTop < bottom

        if (intersects) {
          lastVisibleIndex = it.index
        }
      }

      const start = Math.min(lastVisibleIndex + 1, feedPosts.length)
      const end = Math.min(start + PREFETCH_AHEAD, feedPosts.length)

      if (start >= end) return

      const slice = feedPosts.slice(start, end)
      if (!slice.length) return

      const ids = slice.map((p: any) => p?.id).filter(Boolean)

      const windowKey =
        `${cacheKey}:${start}-${end}:${ids[0] ?? 'x'}:${ids[ids.length - 1] ?? 'y'}`

      const lastWindowKey = getPrefetchWindow(cacheKey) ?? ''
      if (windowKey === lastWindowKey) return

      setPrefetchWindow(cacheKey, windowKey)

      if (prefetchAbortRef.current) {
        prefetchAbortRef.current.abort()
      }

      const ac = new AbortController()
      prefetchAbortRef.current = ac

      prefetchUpcomingPosts(slice, ac.signal).catch(() => {})
    }, PREFETCH_THROTTLE_MS)
  }

  schedulePrefetch()

  const onScroll = () => schedulePrefetch()

  window.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    window.removeEventListener('scroll', onScroll)

    if (prefetchTimerRef.current != null) {
      window.clearTimeout(prefetchTimerRef.current)
      prefetchTimerRef.current = null
    }

    prefetchAbortRef.current?.abort()
  }
}, [
  cacheKey,
  feedPosts,
  rowVirtualizer,
  getPrefetchWindow,
  setPrefetchWindow,
])


  return (
    <CustomPullToRefresh
      scrollContainerRef={scrollContainerRef}
      invalidateQueries={[
        pullToRefreshHome,
        async () => {
          const query = queryClient.getQueryCache().find(['storyFeed'])
          if (!query) return
          if (query.isStale()) {
            await queryClient.refetchQueries(['storyFeed'], { active: true, inactive: true })
          }
        },
      ]}
    >
      <Container>
        {showSkeleton ? (
          <HomeFeedSkeleton />
        ) : showEmpty ? (
          <div className="feed">
            <EmptyFeedPost
                listId={effectiveListId}
                isDefaultList={effectiveListId === 'all'}
            />
          </div>
        ) : (
          <div className="feed">
            <VirtualInner style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
              {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => {
                const post = feedPosts[virtualRow.index]
                if (!post) return null

                const postId = (post as any)?.id
                const reactKey = postId != null ? String(postId) : String(virtualRow.key)

                return (
                  <VirtualRow
                    key={reactKey}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <div style={{ paddingBottom: '1.5rem', boxSizing: 'border-box' }}>
                      <FeedRow
                        post={post}
                        index={virtualRow.index}
                        dataQuery={dataQuery}
                        addDebounceImpression={addDebounceImpression}
                        userData={userData}
                        userId={userId}
                      />
                    </div>
                  </VirtualRow>
                )
              })}
            </VirtualInner>
          </div>
        )}
        <div className='scrollToTop'>
        <ScrollToTopFab scrollContainerRef={scrollContainerRef} estimatedRowPx={ESTIMATED_ROW_PX} showAfterRows={6}/>
        </div>
        {feedPosts.length > 0 && (
          <MoreFeed>
            {isFetchingNextPage ? (
              <BeatLoader size={8} />
            ) : hasNextPage ? (
              ''
            ) : (
              <CaughtUpMessage />
            )}
          </MoreFeed>
        )}
      </Container>
    </CustomPullToRefresh>
  )
}

const Container = styled.div`
  flex: 1;
  padding: 0rem 1rem;
  border-radius: 2.5rem 2.5rem 0 0;
  background-color: #fff;
`

const MoreFeed = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 32px;

  & .localLoader {
    position: relative;
    margin-bottom: 4rem;
  }
`

const VirtualInner = styled.div`
  position: relative;
  width: 100%;
`

const VirtualRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  will-change: transform;

  box-sizing: border-box;
`
